import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { fetchProducts, type ShopifyProduct } from "@/lib/shopify";
import { ProductCard } from "@/components/ProductCard";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import { SITE_URL } from "@/lib/seo";
import { COLOR_CATEGORIES } from "@/lib/colors";
import { SIZE_BUCKETS } from "@/lib/sizes";
import { trackSearch } from "@/lib/analytics";

type Search = { q: string };

export const Route = createFileRoute("/sog")({
  // Lightweight inline validator — keeps the route runtime-safe without
  // pulling in @tanstack/zod-adapter for a single string param.
  validateSearch: (raw: Record<string, unknown>): Search => {
    const q = typeof raw.q === "string" ? raw.q.trim() : "";
    return { q: q.slice(0, 120) };
  },
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: async ({ deps }) => {
    if (!deps.q) return { q: "", products: [] as ShopifyProduct[] };
    const products = await fetchProducts({
      first: 60,
      query: deps.q,
      sortKey: "RELEVANCE",
      reverse: false,
    });
    return { q: deps.q, products };
  },
  head: ({ loaderData }) => {
    const q = loaderData?.q ?? "";
    const title = q
      ? `Søg: »${q}« — Farveladeland`
      : "Søg — Farveladeland";
    return {
      meta: [
        { title },
        { name: "description", content: q ? `Søgeresultater for »${q}« hos Farveladeland.` : "Søg i hele Farveladelands sortiment." },
        // Search results pages should not be indexed
        { name: "robots", content: "noindex,follow" },
        { rel: "canonical", href: `${SITE_URL}/sog` } as never,
      ],
    };
  },
  component: SearchResultsPage,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="mx-auto max-w-2xl px-6 pt-40 pb-32 text-center">
        <p className="eyebrow">Ups</p>
        <h1 className="mt-4 font-display text-5xl">Noget gik galt</h1>
        <p className="mt-4 text-muted-foreground">{error.message}</p>
        <button
          onClick={() => {
            router.invalidate();
            reset();
          }}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 text-xs font-medium uppercase tracking-[0.22em] text-background"
        >
          Prøv igen
        </button>
      </div>
    );
  },
});

function SearchResultsPage() {
  const { q, products } = Route.useLoaderData();
  const { t, locale } = useT();

  // Fire a search_submitted event whenever the query changes.
  useEffect(() => {
    if (q) trackSearch(q, products.length);
  }, [q, products.length]);

  return (
    <>
      <section className="pt-40 pb-10 md:pt-48 md:pb-14">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <p className="eyebrow">{t("search.title")}</p>
          {q ? (
            <>
              <h1 className="mt-3 font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95]">
                {t("search.resultsFor", { q })}
              </h1>
              <p className="mt-4 text-sm uppercase tracking-[0.22em] text-muted-foreground">
                {t("search.resultsCount", { n: products.length })}
              </p>
            </>
          ) : (
            <h1 className="mt-3 font-display text-[clamp(2.5rem,6vw,5rem)] leading-[0.95]">
              {t("header.search")}
            </h1>
          )}
        </div>
      </section>

      {q && products.length > 0 && (
        <section className="mx-auto max-w-[1500px] px-6 pb-24 md:px-10 md:pb-32">
          <div className="grid grid-cols-2 gap-x-4 gap-y-12 sm:grid-cols-3 lg:grid-cols-4">
            {products.map((p: ShopifyProduct) => (
              <ProductCard key={p.node.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {q && products.length === 0 && (
        <section className="mx-auto max-w-2xl px-6 pb-24 text-center md:pb-32">
          <p className="font-display text-3xl">{t("search.noResults")}</p>
          <p className="mt-3 text-muted-foreground">{t("search.tryAnother")}</p>
        </section>
      )}

      {/* Always-on browse fallbacks */}
      <section className="mx-auto max-w-[1500px] border-t border-border px-6 py-16 md:px-10 md:py-24">
        <p className="eyebrow">{t("search.colors")}</p>
        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
          {COLOR_CATEGORIES.map((c) => (
            <LocaleLink
              key={c.slug}
              to="/farver/$slug"
              params={{ slug: c.slug }}
              className="group flex items-center gap-2 rounded-sm border border-border px-3 py-2 hover:border-foreground/40"
            >
              <span
                className="block h-3.5 w-3.5 rounded-full border border-foreground/15"
                style={{ background: c.swatch }}
                aria-hidden
              />
              <span className="text-sm text-foreground group-hover:text-terracotta">
                {locale === "da" ? c.labelDa : c.labelEn}
              </span>
            </LocaleLink>
          ))}
        </div>

        <p className="eyebrow mt-12">{t("search.sizes")}</p>
        <div className="mt-6 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {SIZE_BUCKETS.map((b) => (
            <LocaleLink
              key={b.slug}
              to="/storrelse/$range"
              params={{ range: b.slug }}
              className={`group flex items-center justify-between rounded-sm p-3 ${b.panel}`}
            >
              <span className="text-sm text-foreground">
                {locale === "da" ? b.labelDa : b.labelEn}
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-foreground/55">
                {b.italian}
              </span>
            </LocaleLink>
          ))}
        </div>
      </section>
    </>
  );
}
